import * as Actions from '@/app/store/actions';

import { CircularProgress, Divider } from '@material-ui/core';
import { Controller, useForm, useWatch } from 'react-hook-form';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	createIncidentReport,
	getStudentReportDetails,
	updateIncidentReport,
} from '@/app/services/incidentReports/incidentReports';

import BackButton from '@/app/customComponents/BackButton/BackButton';
import CancelIcon from '@shared/icons/CancelIcon';
import GradientButton from '@shared/components/GradientButton';
import IncidentReportIcon from '@shared/icons/IncidentReportIcon';
import SaveIcon from '@shared/icons/SaveIcon';
import StyledInput from '@shared/components/StyledInput';
import StyledSearchSelect from 'shared/components/StyledSearchSelect';
import StyledSelect from '@shared/components/StyledSelect';
import { XMarkIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { format } from 'date-fns';
import { getBranchDeepLink } from '@/utils/utils';
import { getStudents } from '@/app/services/students/students';
import { states } from '@shared/constants/states';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const IndicentReportForm = ({
	isAdmin = false,
	teacherToken,
	isEdit = false,
	reportData = null,
	handleGoBack = () => {},
}) => {
	const handleIOSRedirect = (action) => {
		if (!isAdmin) {
			const url = getBranchDeepLink(action);
			if (url) {
				window.location.href = url;
			}
		}
	};

	const [foundStudents, setFoundStudents] = useState([]);
	const [studentsLoading, setStudentsLoading] = useState(false);
	const [selectedStudent, setSelectedStudent] = useState(null);
	const [studentReportDetailsLoading, setStudentReportDetailsLoading] = useState(false);
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const searchTimeout = useRef(null);
	const [roomOptions, setRoomOptions] = useState([]);
	const [teacherOptions, setTeacherOptions] = useState([]);
	const [contactOptions, setContactOptions] = useState([]);
	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
		setError,
	} = useForm({
		defaultValues: {
			first_name: '',
			last_name: '',
			age: '',
			gender: '',
			incident_date: format(new Date(), 'yyyy-MM-dd'),
			incident_time: '',
			name_of_center: '',
			room_id: '',
			address: '',
			street_line_2: '',
			state: '',
			city: '',
			zip_code: '',
			description: '',
			injury: '',
			product: '',
			action_taken: '',
			staff_id: '',
			other_staff_id: '',
			contacted: '',
			contact_time: '',
			contact_id: '',
		},
	});

	useEffect(() => {
		if (isEdit && reportData) {
			setSelectedStudent({
				label: `${reportData.student.first_name} ${reportData.student.last_name}`,
				value: reportData.student.id,
			});
		}
	}, [isEdit, reportData]);

	const selectedTeacher = useWatch({ control, name: 'staff_id' });
	const otherSelectedTeacher = useWatch({ control, name: 'other_staff_id' });
	const contacted = useWatch({ control, name: 'contacted' });

	// Reset contact_time, contact_id, and contact_name when contacted is set to no
	useEffect(() => {
		if (contacted === 'no') {
			reset((formValues) => ({
				...formValues,
				contact_time: '',
				contact_id: '',
				contact_name: '',
			}));
		}
	}, [contacted, reset]);

	useEffect(() => {
		if (selectedStudent) {
			setStudentReportDetailsLoading(true);

			if (isAdmin) {
				getStudentReportDetails(selectedStudent.value)
					.then((res) => {
						// Format rooms data
						const formattedRooms = res.data.rooms.map((room) => ({
							label: room.name,
							value: room.id,
						}));
						setRoomOptions(formattedRooms);

						// Format teachers data
						const formattedTeachers = res.data.teachers.map((teacher) => ({
							label: `${teacher.first_name} ${teacher.last_name}`,
							value: teacher.id,
						}));
						setTeacherOptions(formattedTeachers);

						// Format contacts data
						const formattedContacts = res.data.contacts.map((contact) => ({
							label: `${contact.first_name} ${contact.last_name}`,
							value: contact.id,
						}));
						setContactOptions(formattedContacts);

						// Reset form with new values
						reset({
							first_name: res.data.student.first_name,
							last_name: res.data.student.last_name,
							age: res.data.student.age,
							gender: res.data.student.gender,
							name_of_center: res.data.school.name,
							address: res.data.school.address,
							room_id: isEdit ? reportData.room_id : res.data.student.checkin_room,
							street_line_2: res.data.school.street,
							state: res.data.school.state_id,
							city: res.data.school.city,
							zip_code: res.data.school.zip_code,
							description: isEdit ? reportData.description : res.data.description,
							injury: isEdit ? reportData.injury : res.data.injury,
							product: isEdit ? reportData.product : res.data.product,
							action_taken: isEdit ? reportData.action_taken : res.data.action_taken,
							staff_id: isEdit ? reportData.staff_id : undefined,
							other_staff_id: isEdit ? reportData.other_staff_id : undefined,
							contacted: isEdit ? (reportData.contacted ? 'yes' : 'no') : undefined,
							contact_time: isEdit ? reportData.contact_time : res.data.contact_time,
							contact_id: isEdit ? reportData.contact_id : res.data.contact_id,
							incident_time: isEdit ? reportData.incident_time : res.data.incident_time,
							incident_date: isEdit ? reportData.incident_date : res.data.incident_date,
						});

						setStudentReportDetailsLoading(false);
					})
					.catch(() => setStudentReportDetailsLoading(false));
			} else {
				const axiosInstance = axios.create({
					headers: {
						Authorization: `Bearer ${teacherToken}`,
					},
				});

				axiosInstance
					.get(`/api/v2/incident/filters/${selectedStudent.value}`)
					.then((res) => {
						// Format rooms data
						const formattedRooms = res.data.rooms.map((room) => ({
							label: room.name,
							value: room.id,
						}));
						setRoomOptions(formattedRooms);

						// Format teachers data
						const formattedTeachers = res.data.teachers.map((teacher) => ({
							label: `${teacher.first_name} ${teacher.last_name}`,
							value: teacher.id,
						}));
						setTeacherOptions(formattedTeachers);

						// Format contacts data
						const formattedContacts = res.data.contacts.map((contact) => ({
							label: `${contact.first_name} ${contact.last_name}`,
							value: contact.id,
						}));
						setContactOptions(formattedContacts);

						// Reset form with new values
						reset({
							first_name: res.data.student.first_name,
							last_name: res.data.student.last_name,
							age: res.data.student.age,
							gender: res.data.student.gender,
							name_of_center: res.data.school.name,
							address: res.data.school.address,
							street_line_2: res.data.school.street,
							state: res.data.school.state_id,
							city: res.data.school.city,
							zip_code: res.data.school.zip_code,
						});

						setStudentReportDetailsLoading(false);
					})
					.catch(() => setStudentReportDetailsLoading(false));
			}
		}
	}, [selectedStudent, isAdmin, teacherToken, reset]);

	const navigate = useNavigate();

	const handleClose = () => {
		if (!isAdmin) {
			handleIOSRedirect('close');
		} else {
			isEdit ? handleGoBack() : navigate(-1);
		}
	};

	const onSubmit = (data) => {
		setSubmitting(true);
		console.log('incident_date value:', data.incident_date);
		console.log('incident_date type:', typeof data.incident_date);
		// Day.js object from DatePicker - format to YYYY-MM-DD string
		const formattedDate = data.incident_date && data.incident_date.format
			? data.incident_date.format('YYYY-MM-DD')
			: data.incident_date || '';

		// Check if date is in the future
		const today = format(new Date(), 'yyyy-MM-dd');
		if (formattedDate > today) {
			setError('incident_date', {
				type: 'manual',
				message: 'Incident date cannot be in the future',
			});
			return;
		}

		const payload = {
			student_id: selectedStudent?.value,
			incident_date: formattedDate,
			incident_time: data.incident_time,
			room_id: data.room_id,
			description: data.description,
			injury: data.injury,
			product: data.product,
			action_taken: data.action_taken,
			staff_id: data.staff_id,
			other_staff_id: data.other_staff_id,
			contacted: data.contacted === 'yes',
			contact_time: data.contact_time,
			contact_id: data.contact_id,
			contact_name: data.contact_name,
		};

		if (isAdmin) {
			if (isEdit) {
				updateIncidentReport(reportData.id, payload)
					.then(() => {
						dispatch(
							Actions.showMessage({
								message: 'Incident report updated successfully',
								autoHideDuration: 1500,
								variant: 'success',
							})
						);
						handleGoBack();
						setSubmitting(false);
					})
					.catch((error) => {
						console.error('Error submitting incident report:', error);
						if (error.response?.data?.errors) {
							Object.keys(error.response.data.errors).forEach((key) => {
								setError(key, {
									type: 'manual',
									message: error.response.data.errors[key][0],
								});
							});
						}
					});
			} else {
				createIncidentReport(payload)
					.then(() => {
						dispatch(
							Actions.showMessage({
								message: 'Incident report submitted successfully',
								autoHideDuration: 1500,
								variant: 'success',
							})
						);
						navigate(-1);
						setSubmitting(false);
					})
					.catch((error) => {
						console.error('Error submitting incident report:', error);
						if (error.response?.data?.errors) {
							Object.keys(error.response.data.errors).forEach((key) => {
								setError(key, {
									type: 'manual',
									message: error.response.data.errors[key][0],
								});
							});
						}
					});
			}
		} else {
			const axiosInstance = axios.create({
				headers: {
					Authorization: `Bearer ${teacherToken}`,
				},
			});

			axiosInstance
				.post('/api/v2/incident', payload)
				.then(() => {
					setSubmitting(false);
					handleIOSRedirect('success');
				})
				.catch((error) => {
					console.error('Error submitting incident report:', error);
					if (error.response?.data?.errors) {
						Object.keys(error.response.data.errors).forEach((key) => {
							setError(key, {
								type: 'manual',
								message: error.response.data.errors[key][0],
							});
						});
					}
				});
		}
	};

	const performSearch = useCallback(
		(query) => {
			setStudentsLoading(true);

			if (isAdmin) {
				getStudents(query)
					.then((res) => {
						const formattedStudents = res.data.data.map((student) => ({
							label: `${student.first_name} ${student.last_name}`,
							value: student.id,
						}));
						setFoundStudents(formattedStudents);
						setStudentsLoading(false);
					})
					.catch(() => setStudentsLoading(false));
			} else {
				const url = `/api/v1/school/tablet/student/search?q=${query}&app=true&status=all`;
				const axiosInstance = axios.create({
					headers: {
						Authorization: `Bearer ${teacherToken}`,
					},
				});
				axiosInstance
					.get(url)
					.then((res) => {
						const formattedStudents = res.data.map((student) => ({
							label: `${student.first_name} ${student.last_name}`,
							value: student.id,
						}));
						setFoundStudents(formattedStudents);
						setStudentsLoading(false);
					})
					.catch(() => setStudentsLoading(false));
			}
		},
		[isAdmin, teacherToken]
	);

	const handleSearchStudents = (query) => {
		setSearchQuery(query);
	};

	useEffect(() => {
		// Clear any existing timeout
		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		// Don't search if query is empty
		if (!searchQuery.trim()) {
			setFoundStudents([]);
			return;
		}

		// Set new timeout
		searchTimeout.current = setTimeout(() => {
			performSearch(searchQuery);
		}, 500);

		// Cleanup timeout on unmount or when searchQuery changes
		return () => {
			if (searchTimeout.current) {
				clearTimeout(searchTimeout.current);
			}
		};
	}, [searchQuery, performSearch]);

	const handleClearOtherTeacher = () => {
		reset((formValues) => ({
			...formValues,
			other_staff_id: '',
		}));
	};

	return (
		<div className={`bg-white ${isAdmin ? 'mb-80' : 'overflow-scroll'}`}>
			{studentReportDetailsLoading ? (
				<div className="absolute top-0 left-0 w-full h-full flex justify-center items-center z-20 bg-black/20">
					<CircularProgress />
				</div>
			) : null}
			<div className="flex flex-col gap-24 p-24">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-16">
						{isAdmin ? <BackButton onClick={handleClose} /> : null}
						<IncidentReportIcon />
						<div>
							<h1 className="text-24 font-inter font-bold">Incident Report</h1>
							<span className="text-[#6B7280] text-16">
								{isEdit ? 'Edit Incident Report' : 'Select student to report an incident'}
							</span>
						</div>
					</div>
					<div className="flex items-center gap-16 justify-end">
						{!isAdmin ? (
							<button className="p-8 rounded-md" onClick={handleClose} type="button">
								<XMarkIcon className="size-24 text-[#2563EB]" />
							</button>
						) : null}
					</div>
				</div>
				<div className={`w-full mt-16 flex justify-end ${isEdit ? 'hidden' : ''}`}>
					<StyledSearchSelect
						control={control}
						fullWidth
						label="Select Student"
						labelClasses="text-black"
						name="teacher_token"
						onChange={(e) => {
							setSelectedStudent(e);
						}}
						onInputChange={handleSearchStudents}
						options={foundStudents || []}
						required
						resultsLoading={studentsLoading}
						value={selectedStudent}
						widthClass="w-full"
					/>
				</div>
			</div>
			<Divider className="mb-56" />

			<form className="relative" onSubmit={handleSubmit(onSubmit)}>
				{/* <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
					<CircularProgress />
				</div> */}
				<div className="flex flex-col gap-48 px-24 pb-48">
					<div className="flex flex-col sm:flex-row gap-36 sm:gap-24">
						<div className="w-full sm:w-[40%]">
							<Controller
								control={control}
								name="first_name"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.first_name}
										fullWidth
										helperText={errors?.first_name?.message}
										label="Student First Name"
										labelClasses="text-black"
										placeholder="Enter first name"
										required
									/>
								)}
								rules={{
									required: 'First name is required',
								}}
							/>
						</div>
						<div className="w-full sm:w-[40%]">
							<Controller
								control={control}
								name="last_name"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.last_name}
										fullWidth
										helperText={errors?.last_name?.message}
										label="Student Last Name"
										labelClasses="text-black"
										placeholder="Enter last name"
										required
									/>
								)}
								rules={{
									required: 'Last name is required',
								}}
							/>
						</div>
						<div className="w-full sm:w-[20%]">
							<Controller
								control={control}
								name="age"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.age}
										fullWidth
										helperText={errors?.age?.message}
										label="Age"
										labelClasses="text-black"
										placeholder="Enter age"
										required
										type="number"
									/>
								)}
								rules={{
									required: 'Age is required',
									pattern: {
										value: /^[0-9]+$/,
										message: 'Please enter a valid age',
									},
								}}
							/>
						</div>
					</div>
					<div className="flex flex-col sm:flex-row gap-36 sm:gap-24">
						<div className="w-full sm:w-[20%]">
							<Controller
								control={control}
								name="gender"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled
										error={!!errors.gender}
										fullWidth
										helperText={errors?.gender?.message}
										label="Gender"
										labelClasses="text-black"
										options={[
											{ label: 'Male', value: 'male' },
											{ label: 'Female', value: 'female' },
										]}
										required
									/>
								)}
								rules={{
									required: 'Gender is required',
								}}
							/>
						</div>
						<div className="w-full sm:w-[40%]">
							<Controller
								control={control}
								name="incident_date"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!selectedStudent}
										error={!!errors.incident_date}
										fullWidth
										helperText={errors?.incident_date?.message}
										label="Date of Incident"
										labelClasses="text-black"
										max={format(new Date(), 'yyyy-MM-dd')}
										placeholder="Select date"
										required
										type="date"
									/>
								)}
								rules={{
									required: 'Date of incident is required',
									validate: (value) => {
										if (new Date(value) > new Date()) {
											return 'Incident date cannot be in the future';
										}
										return true;
									},
								}}
							/>
						</div>
						<div className="w-full sm:w-[40%]">
							<Controller
								control={control}
								name="incident_time"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!selectedStudent}
										error={!!errors.incident_time}
										fullWidth
										helperText={errors?.incident_time?.message}
										label="Time of Incident"
										labelClasses="text-black"
										placeholder="Select time"
										required
										type="time"
									/>
								)}
								rules={{
									required: 'Time of incident is required',
								}}
							/>
						</div>
					</div>
					<div className="flex flex-col sm:flex-row gap-36 sm:gap-24">
						<div className="w-full sm:w-[50%]">
							<Controller
								control={control}
								name="name_of_center"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.name_of_center}
										fullWidth
										helperText={errors?.name_of_center?.message}
										label="Name of Center"
										labelClasses="text-black"
										placeholder="Enter center name"
										required
									/>
								)}
								rules={{
									required: 'Name of center is required',
								}}
							/>
						</div>
						<div className="w-full sm:w-[50%]">
							<Controller
								control={control}
								name="room_id"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled={!selectedStudent}
										error={!!errors.room_id}
										fullWidth
										helperText={errors?.room_id?.message}
										label="Room/Location of Incident"
										labelClasses="text-black"
										options={roomOptions}
										required
									/>
								)}
								rules={{
									required: 'Room/Location is required',
								}}
							/>
						</div>
					</div>
					<div className="flex flex-col sm:flex-row gap-36 sm:gap-24">
						<div className="w-full md:w-[50%]">
							<Controller
								control={control}
								name="address"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.address}
										fullWidth
										helperText={errors?.address?.message}
										label="Address of Center"
										labelClasses="text-black"
										placeholder="Enter address"
										required
									/>
								)}
								rules={{
									required: 'Address is required',
								}}
							/>
						</div>
						<div className="w-full md:w-[50%]">
							<Controller
								control={control}
								name="street_line_2"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										fullWidth
										label="Street Line 2"
										labelClasses="text-black"
										placeholder="Enter additional address info"
									/>
								)}
							/>
						</div>
					</div>
					<div className="flex flex-col sm:flex-row gap-36 sm:gap-24">
						<div className="w-full sm:w-[33.33%]">
							<Controller
								control={control}
								name="state"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled
										error={!!errors.state}
										fullWidth
										helperText={errors?.state?.message}
										label="State"
										labelClasses="text-black"
										options={states}
										required
									/>
								)}
								rules={{
									required: 'State is required',
								}}
							/>
						</div>
						<div className="w-full sm:w-[33.33%]">
							<Controller
								control={control}
								name="city"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.city}
										fullWidth
										helperText={errors?.city?.message}
										label="City"
										labelClasses="text-black"
										placeholder="Enter city"
										required
									/>
								)}
								rules={{
									required: 'City is required',
								}}
							/>
						</div>
						<div className="w-full sm:w-[33.33%]">
							<Controller
								control={control}
								name="zip_code"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled
										error={!!errors.zip_code}
										fullWidth
										helperText={errors?.zip_code?.message}
										label="Zip Code"
										labelClasses="text-black"
										placeholder="Enter zip code"
										required
									/>
								)}
								rules={{
									required: 'Zip code is required',
								}}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-72">
						<div className={`w-full relative ${errors.description ? 'pb-16' : ''}`}>
							<Controller
								control={control}
								name="description"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!selectedStudent}
										error={!!errors.description}
										fullWidth
										helperText={errors?.description?.message}
										label="Detailed explanation of the incident"
										labelClasses="text-black"
										maxRows={8}
										minRows={4}
										multiline
										placeholder="Enter a detailed explanation..."
										required
									/>
								)}
								rules={{
									required: 'Incident details are required',
								}}
							/>
							<span className="text-14 text-[#6B7280] absolute -bottom-[24px] left-0">
								Include student&apos;s report of the incident.
							</span>
						</div>
						<div className={`w-full relative ${errors.injury ? 'pb-16' : ''}`}>
							<Controller
								control={control}
								name="injury"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!selectedStudent}
										error={!!errors.injury}
										fullWidth
										helperText={errors?.injury?.message}
										label="Detailed description of the injury"
										labelClasses="text-black"
										maxRows={8}
										minRows={4}
										multiline
										placeholder="Enter a detailed description..."
										required
									/>
								)}
								rules={{
									required: 'Injury details are required',
								}}
							/>
							<span className="text-14 text-[#6B7280] absolute -bottom-[24px] left-0">
								Include specific area of body involved.
							</span>
						</div>
						<div className="w-full relative">
							<Controller
								control={control}
								name="product"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!selectedStudent}
										error={!!errors.product}
										fullWidth
										helperText={errors?.product?.message}
										label="Description of consumer product involved (if applicable)"
										labelClasses="text-black"
										maxRows={8}
										minRows={4}
										multiline
										placeholder="Enter a detailed explanation..."
									/>
								)}
							/>
						</div>
						<div className="w-full relative">
							<Controller
								control={control}
								name="action_taken"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!selectedStudent}
										error={!!errors.action_taken}
										fullWidth
										helperText={errors?.action_taken?.message}
										label="Action taken on behalf of injured student. (i.e. medical treatment, first aid...) "
										labelClasses="text-black"
										maxRows={8}
										minRows={4}
										multiline
										placeholder="Enter action taken..."
										required
									/>
								)}
								rules={{
									required: 'Action taken is required',
								}}
							/>
						</div>
					</div>
					<div className="flex flex-col md:flex-row gap-48 md:gap-24">
						<div className="w-full md:w-[50%]">
							<Controller
								control={control}
								name="staff_id"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled={!selectedStudent}
										error={!!errors.staff_id}
										fullWidth
										helperText={errors?.staff_id?.message}
										label="Name of caregiver in charge at the time of incident"
										labelClasses="text-black"
										options={teacherOptions?.filter((teacher) => teacher.value !== otherSelectedTeacher)}
										required
									/>
								)}
								rules={{
									required: 'Caregiver name is required',
								}}
							/>
						</div>
						<div className="w-full md:w-[50%] relative">
							<Controller
								control={control}
								name="other_staff_id"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled={!selectedStudent}
										error={!!errors.other_staff_id}
										fullWidth
										helperText={errors?.other_staff_id?.message}
										label="Name of caregiver in charge at the time of incident"
										labelClasses="text-black"
										options={teacherOptions?.filter((teacher) => {
											return teacher.value !== selectedTeacher;
										})}
									/>
								)}
							/>
							{otherSelectedTeacher ? (
								<span
									className="text-14 text-pd-blue absolute -bottom-[24px] right-0 -top-24 select-none cursor-pointer"
									onClick={handleClearOtherTeacher}
								>
									Clear
								</span>
							) : null}
						</div>
					</div>
					{isAdmin ? (
						<div className="flex flex-col md:flex-row gap-48 md:gap-24">
							<div className="w-full md:w-[50%]">
								<Controller
									control={control}
									name="contacted"
									render={({ field }) => (
										<StyledSelect
											{...field}
											disabled={!selectedStudent}
											error={!!errors.contacted}
											fullWidth
											helperText={errors?.contacted?.message}
											label="Was a parent or guardian contacted?"
											labelClasses="text-black"
											options={[
												{ label: 'Yes', value: 'yes' },
												{ label: 'No', value: 'no' },
											]}
											required
										/>
									)}
									rules={{
										required: 'This field is required',
									}}
								/>
							</div>
							<div className="w-full md:w-[50%]">
								<Controller
									control={control}
									name="contact_time"
									render={({ field }) => (
										<StyledInput
											{...field}
											disabled={!selectedStudent}
											error={!!errors.contact_time}
											fullWidth
											helperText={errors?.contact_time?.message}
											label="If yes, what time?"
											labelClasses="text-black"
											type="time"
										/>
									)}
								/>
							</div>
							<div className="w-full md:w-[50%]">
								<Controller
									control={control}
									name="contact_id"
									render={({ field }) => (
										<StyledSelect
											{...field}
											disabled={!selectedStudent}
											error={!!errors.contact_id}
											fullWidth
											helperText={errors?.contact_id?.message}
											label="Name of parent/guardian contacted"
											labelClasses="text-black"
											options={contactOptions}
										/>
									)}
								/>
							</div>
						</div>
					) : null}
				</div>
				<div className="flex items-center justify-between gap-12 px-32 py-16 border-t shadow-profile-section flex-wrap">
					{!selectedStudent ? (
						<span className="text-14 text-red-500">Please select a student from the list above</span>
					) : Object.keys(errors).length > 0 ? (
						<span className="text-14 text-red-500">Please check the form for errors</span>
					) : (
						<div />
					)}
					<div className="flex gap-12 min-w-[275px]">
						<GradientButton onClick={handleClose} type="button" variant="light-blue" width="100%">
							<CancelIcon className="w-24" />
							Cancel
						</GradientButton>
						<GradientButton
							disabled={submitting}
							onClick={handleSubmit(onSubmit)}
							type="submit"
							variant="light-blue"
							width="100%"
						>
							<SaveIcon className="w-24" />
							Save
						</GradientButton>
					</div>
				</div>
			</form>
		</div>
	);
};

export default IndicentReportForm;
